import React from 'react';

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-lg font-black text-white uppercase tracking-wide mb-3 flex items-center gap-2">
      <span className="w-3 h-px bg-f1red inline-block" />{title}
    </h2>
    <div className="text-zinc-400 text-sm leading-relaxed space-y-2">{children}</div>
  </div>
);

const LegalLayout = ({ title, subtitle, children }) => (
  <div className="max-w-3xl mx-auto px-4 py-12">
    <div className="mb-10">
      <p className="f1-label mb-2">Informations légales</p>
      <h1 className="text-4xl md:text-5xl font-black text-white leading-none">{title}</h1>
      {subtitle && <p className="text-zinc-500 text-sm mt-3">{subtitle}</p>}
    </div>
    <div className="border-t border-zinc-800 pt-8">{children}</div>
  </div>
);

/* ─── MENTIONS LÉGALES ──────────────────────────────────────────── */
export const MentionsLegales = () => (
  <LegalLayout title="Mentions légales" subtitle="Dernière mise à jour : avril 2026">
    <Section title="Éditeur du site">
      <p>Le site <strong className="text-white">P1 or Not P1</strong> est un projet personnel non commercial, développé et maintenu par un particulier à titre de loisir.</p>
    </Section>

    <Section title="Propriété intellectuelle">
      <p>Le contenu du site (design, textes, code source) est la propriété de l'éditeur, sauf mention contraire. Toute reproduction est interdite sans autorisation préalable.</p>
      <p>Les noms, logos et marques <strong className="text-white">Formula 1®</strong>, <strong className="text-white">FIA®</strong> ainsi que les noms des écuries et pilotes sont la propriété exclusive de leurs détenteurs respectifs. P1 or Not P1 est un projet de fans indépendant, sans affiliation ni partenariat officiel avec la Formula 1, Formula One Management Ltd, ou la FIA.</p>
    </Section>

    <Section title="Données personnelles">
      <p>Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez d'un droit d'accès, de rectification et de suppression de vos données. Voir notre <a href="/privacy" className="text-f1red hover:underline">Politique de confidentialité</a> pour plus d'informations.</p>
    </Section>

    <Section title="Limitation de responsabilité">
      <p>L'éditeur ne saurait être tenu responsable des dommages directs ou indirects liés à l'utilisation du site. Les informations sportives (résultats, classements, calendrier) sont fournies à titre indicatif et peuvent contenir des erreurs ou retards.</p>
      <p>Le site peut être indisponible temporairement pour maintenance sans préavis.</p>
    </Section>

    <Section title="Loi applicable">
      <p>Les présentes mentions légales sont soumises au droit français. Tout litige relatif à l'utilisation du site relève de la compétence exclusive des tribunaux français.</p>
    </Section>
  </LegalLayout>
);

/* ─── POLITIQUE DE CONFIDENTIALITÉ ─────────────────────────────── */
export const Privacy = () => (
  <LegalLayout title="Politique de confidentialité" subtitle="Dernière mise à jour : avril 2026 · Conforme RGPD">
    <Section title="Responsable du traitement">
      <p><strong className="text-white">P1 or Not P1</strong> — projet personnel non commercial.</p>
    </Section>

    <Section title="Données collectées">
      <p>Lors de la création d'un compte, nous collectons :</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li>Nom d'utilisateur (pseudo)</li>
        <li>Adresse e-mail</li>
        <li>Mot de passe (haché, jamais stocké en clair)</li>
        <li>Pilote favori choisi</li>
        <li>Scores et historique de jeu</li>
        <li>Date et heure d'inscription</li>
      </ul>
      <p className="mt-2">Aucune donnée bancaire, aucun numéro de téléphone n'est collecté.</p>
    </Section>

    <Section title="Finalités du traitement">
      <ul className="list-disc pl-5 space-y-1">
        <li>Authentification et gestion du compte utilisateur</li>
        <li>Calcul et affichage des classements (leaderboard)</li>
        <li>Fonctionnalités sociales (amis, défis)</li>
        <li>Amélioration du service</li>
      </ul>
    </Section>

    <Section title="Base légale">
      <p>Le traitement est fondé sur l'<strong className="text-white">exécution du contrat</strong> (art. 6.1.b RGPD) : votre compte utilisateur constitue la relation contractuelle. Aucune donnée n'est traitée à des fins publicitaires ou commerciales.</p>
    </Section>

    <Section title="Durée de conservation">
      <p>Vos données sont conservées aussi longtemps que votre compte est actif. En cas de suppression de compte, toutes vos données personnelles sont effacées dans un délai de <strong className="text-white">30 jours</strong>. Les scores anonymisés peuvent être conservés à des fins statistiques.</p>
    </Section>

    <Section title="Partage des données">
      <p>Vos données ne sont <strong className="text-white">jamais vendues ni cédées</strong> à des tiers. Elles peuvent être transmises uniquement :</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li>À l'hébergeur technique pour le fonctionnement du site</li>
        <li>Sur réquisition judiciaire</li>
      </ul>
    </Section>

    <Section title="Cookies">
      <p>Le site utilise uniquement un cookie de session pour maintenir votre connexion (<strong className="text-white">cookie fonctionnel strictement nécessaire</strong>). Aucun cookie publicitaire ni tracker tiers n'est utilisé.</p>
    </Section>

    <Section title="Vos droits">
      <p>Conformément au RGPD, vous disposez des droits suivants :</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li><strong className="text-white">Accès</strong> : obtenir une copie de vos données</li>
        <li><strong className="text-white">Rectification</strong> : corriger des données inexactes</li>
        <li><strong className="text-white">Effacement</strong> : supprimer votre compte et vos données</li>
        <li><strong className="text-white">Portabilité</strong> : recevoir vos données dans un format lisible</li>
        <li><strong className="text-white">Opposition</strong> : vous opposer à un traitement</li>
      </ul>
      <p className="mt-2">Pour exercer ces droits, utilisez le formulaire disponible sur la page <a href="/contact" className="text-f1red hover:underline">Contact</a>.</p>
      <p>Vous pouvez également introduire une réclamation auprès de la <strong className="text-white">CNIL</strong> (<a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-f1red hover:underline">cnil.fr</a>).</p>
    </Section>
  </LegalLayout>
);

/* ─── CGU ───────────────────────────────────────────────────────── */
export const CGU = () => (
  <LegalLayout title="Conditions générales d'utilisation" subtitle="En vigueur depuis avril 2026 · Applicables à tous les utilisateurs inscrits">
    <Section title="Article 1 — Objet">
      <p>Les présentes CGU régissent l'accès et l'utilisation du site <strong className="text-white">P1 or Not P1</strong>, plateforme de jeux et pronostics autour de la Formule 1. L'inscription vaut acceptation pleine et entière des présentes conditions.</p>
    </Section>

    <Section title="Article 2 — Accès au service">
      <p>L'inscription est gratuite et ouverte à toute personne physique. Pour les mineurs, l'accord d'un représentant légal est requis. L'éditeur se réserve le droit de refuser ou de suspendre un compte sans préavis en cas de manquement aux présentes CGU.</p>
    </Section>

    <Section title="Article 3 — Compte utilisateur">
      <ul className="list-disc pl-5 space-y-1">
        <li>Chaque utilisateur ne peut posséder qu'un seul compte.</li>
        <li>Le pseudo choisi ne doit pas être offensant, discriminatoire ou usurper l'identité d'une autre personne.</li>
        <li>Vous êtes responsable de la confidentialité de votre mot de passe.</li>
        <li>Tout accès frauduleux à votre compte doit être signalé immédiatement.</li>
      </ul>
    </Section>

    <Section title="Article 4 — Règles des jeux">
      <p>Les jeux disponibles (LightSpeed, CirQuiz, PodiumQuiz, Champion de saison) sont soumis aux règles spécifiques affichées dans chaque jeu. L'éditeur se réserve le droit de modifier les règles, les récompenses XP et les classements à tout moment.</p>
      <p>Toute tentative de triche (manipulation de scores, exploitation de failles techniques) entraîne la suppression immédiate du compte et l'annulation des scores concernés.</p>
    </Section>

    <Section title="Article 5 — Contenu utilisateur">
      <p>Les pseudos et informations de profil visibles par d'autres utilisateurs doivent respecter les lois en vigueur et les règles de bienséance. Sont notamment interdits :</p>
      <ul className="list-disc pl-5 space-y-1 mt-1">
        <li>Les contenus à caractère haineux, discriminatoire ou harcelant</li>
        <li>Les contenus à caractère pornographique ou choquant</li>
        <li>L'usurpation d'identité (de pilotes, personnalités ou autres utilisateurs)</li>
      </ul>
    </Section>

    <Section title="Article 6 — Système de points (XP)">
      <p>Les points d'expérience (XP) sont une unité virtuelle sans valeur monétaire. Ils ne peuvent être échangés, vendus ou transférés. L'éditeur se réserve le droit d'ajuster les barèmes XP et de corriger tout score obtenu de manière frauduleuse.</p>
    </Section>

    <Section title="Article 7 — Disponibilité du service">
      <p>Le service est fourni "en l'état", sans garantie de disponibilité continue. Des interruptions peuvent survenir pour maintenance ou en cas de force majeure. L'éditeur ne saurait être tenu responsable de toute perte de données liée à une interruption.</p>
    </Section>

    <Section title="Article 8 — Modification des CGU">
      <p>L'éditeur se réserve le droit de modifier les présentes CGU à tout moment. Les utilisateurs seront informés des modifications importantes. La poursuite de l'utilisation du service après modification vaut acceptation des nouvelles CGU.</p>
    </Section>

    <Section title="Article 9 — Résiliation">
      <p>Vous pouvez supprimer votre compte à tout moment depuis les paramètres de votre profil. L'éditeur peut résilier un compte en cas de violation des présentes CGU, sans préavis ni remboursement.</p>
    </Section>

    <Section title="Article 10 — Droit applicable">
      <p>Les présentes CGU sont régies par le droit français. Tout litige sera soumis aux tribunaux compétents du ressort de Paris.</p>
    </Section>
  </LegalLayout>
);

/* ─── CONTACT ───────────────────────────────────────────────────── */
export const Contact = () => (
  <LegalLayout title="Contact" subtitle="Une question, un bug, une suggestion ?">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-10">
      {[
        { icon: '✉️', title: 'Support général',        desc: 'Questions sur le site, problèmes de compte, suggestions de fonctionnalités.' },
        { icon: '🔒', title: 'Données personnelles',    desc: 'Droits RGPD, demande de suppression de compte ou d\'export de données.' },
        { icon: '⚖️', title: 'Mentions légales & CGU',  desc: 'Questions juridiques, signalement de contenu non conforme.' },
        { icon: '🐛', title: 'Signaler un bug',         desc: 'Un jeu qui plante, un score incorrect, une erreur d\'affichage.' },
      ].map(c => (
        <div key={c.title}
          className="rounded-lg border border-zinc-800/80 bg-[#0e0e12] p-5">
          <div className="h-[2px] w-8 mb-4" style={{ background: '#e10600' }} />
          <span className="text-2xl mb-3 block">{c.icon}</span>
          <h3 className="text-white font-black text-base mb-1">{c.title}</h3>
          <p className="text-zinc-500 text-xs leading-relaxed">{c.desc}</p>
        </div>
      ))}
    </div>

    <Section title="Comment nous contacter">
      <p>P1 or Not P1 est un projet personnel. Il n'existe pas d'adresse de contact publique pour le moment.</p>
      <p>Pour toute demande urgente liée à vos données personnelles (droit d'accès, suppression de compte), vous pouvez soumettre votre demande directement depuis les paramètres de votre profil.</p>
    </Section>

    <Section title="Signalement de contenu">
      <p>Si vous constatez un pseudo offensant ou un comportement contraire aux CGU, signalez-le depuis la page profil de l'utilisateur concerné.</p>
    </Section>

    <Section title="Projet de fans">
      <p>P1 or Not P1 est un projet indépendant, développé par passion pour la Formule 1. Les suggestions d'amélioration sont les bienvenues via les options disponibles dans l'application.</p>
    </Section>
  </LegalLayout>
);
